import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

interface CancelKeyConfirmOverlayProps {
  open: boolean;
  revoking: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}

export function CancelKeyConfirmOverlay({
  open,
  revoking,
  onConfirm,
  onDismiss,
}: CancelKeyConfirmOverlayProps) {
  const { t } = useTranslation('instances');

  return (
    <Box
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="cancel-key-title"
      aria-describedby="cancel-key-text"
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3.5,
        background: th => alpha(th.palette.background.paper, 0.55),
        backdropFilter: 'blur(2px)',
        opacity: open ? 1 : 0,
        visibility: open ? 'visible' : 'hidden',
        pointerEvents: open ? 'auto' : 'none',
        transition: open
          ? 'opacity 280ms ease'
          : 'opacity 280ms ease, visibility 0s linear 280ms',
      }}
    >
      {/* Confirm card */}
      <Box
        sx={th => ({
          width: '100%',
          maxWidth: 440,
          bgcolor: th.palette.background.paper,
          borderRadius: th.borderRadius.sm,
          border: `1px solid ${th.palette.divider}`,
          boxShadow: `0 24px 60px ${alpha(th.palette.error.main, 0.18)}, 0 8px 22px ${alpha(th.palette.common.black, 0.12)}`,
          pt: 4,
          px: 4,
          pb: 3,
          textAlign: 'center',
          transform: open ? 'none' : 'translateY(14px) scale(0.96)',
          opacity: open ? 1 : 0,
          transition: open
            ? 'transform 320ms cubic-bezier(.2,.85,.25,1) 60ms, opacity 280ms ease 60ms'
            : 'transform 320ms cubic-bezier(.2,.85,.25,1), opacity 280ms ease',
        })}
      >
        {/* Warning icon with pulse */}
        <Box
          aria-hidden="true"
          sx={th => ({
            width: 68,
            height: 68,
            borderRadius: '50%',
            mx: 'auto',
            mb: 2,
            display: 'grid',
            placeItems: 'center',
            color: th.palette.error.main,
            background: `radial-gradient(circle at 50% 40%, ${alpha(th.palette.error.light, 0.5)} 0%, ${alpha(th.palette.error.light, 0.15)} 100%)`,
            animation: 'confirmPulse 1.8s ease-out infinite',
            '@keyframes confirmPulse': {
              '0%': { boxShadow: `0 0 0 0 ${alpha(th.palette.error.main, 0.30)}` },
              '70%': { boxShadow: `0 0 0 14px ${alpha(th.palette.error.main, 0)}` },
              '100%': { boxShadow: `0 0 0 0 ${alpha(th.palette.error.main, 0)}` },
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          })}
        >
          <WarningAmberIcon sx={{ fontSize: 32 }} />
        </Box>

        {/* Title */}
        <Typography
          id="cancel-key-title"
          variant="h6"
          sx={th => ({ fontWeight: th.typography.fontWeightBold, letterSpacing: '-0.01em', mb: 1 })}
        >
          {t('revokeKey.confirmTitle')}
        </Typography>

        {/* Description */}
        <Typography
          id="cancel-key-text"
          variant="body2"
          sx={th => ({ color: th.palette.common.black, lineHeight: 1.55, maxWidth: 360, mx: 'auto', mb: 1 })}
        >
          {t('revokeKey.confirmText')}
        </Typography>


        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
          <Button
            autoFocus
            fullWidth
            variant="outlined"
            onClick={onDismiss}
            disabled={revoking}
            sx={th => ({
              height: th.button.md,
              borderRadius: th.borderRadius.md,
              textTransform: 'none',
              fontWeight: th.typography.fontWeightBold,
              borderWidth: '1.5px',
              borderColor: th.palette.primary.main,
              color: th.palette.text.primary,
              '&:hover': { bgcolor: th.palette.grey[50], borderWidth: '1.5px' },
            })}
          >
            {t('revokeKey.confirmNo')}
          </Button>
          <Button
            fullWidth
            onClick={onConfirm}
            disabled={revoking}
            sx={th => ({
              height: th.button.md,
              borderRadius: th.borderRadius.md,
              textTransform: 'none',
              fontWeight: th.typography.fontWeightSemiBold,
              color: th.palette.common.white,
              background: `linear-gradient(135deg, ${th.palette.error.light} 0%, ${th.palette.error.main} 100%)`,
              boxShadow: `0 1px 2px ${alpha(th.palette.common.black, 0.10)}, 0 8px 18px ${alpha(th.palette.error.main, 0.32)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${th.palette.error.light} 0%, ${th.palette.error.main} 100%)`,
                boxShadow: `0 1px 2px ${alpha(th.palette.common.black, 0.10)}, 0 10px 24px ${alpha(th.palette.error.main, 0.42)}`,
              },
              '&:disabled': { opacity: 0.6 },
            })}
          >
            {t('revokeKey.confirmYes')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
